/**
 * Label Clusters Map Preview Generator.
 *
 * Generates a live map preview of PS label clusters by fetching data directly from the Label Clusters API.
 *
 * @requires DOM element with id 'label-clusters-preview'
 * @requires mapbox-gl and js/api-docs/apiDocsMap.js
 */

import { geometryBounds } from '../common/geoBounds.js';
import { util } from '../common/utilities.js';
import { ApiDocsMap } from './apiDocsMap.js';
import { ApiDocsTheme } from './apiDocsTheme.js';

const CLUSTERS_LAYER = 'label-clusters';
const REGION_SOURCE = 'preview-region';

// Presentational only — it mirrors no backend value, and is picked to stay distinct from every label type color
// against the dimmed basemap.
const REGION_COLOR = ApiDocsTheme.color('--color-link-200');

export let config = {
  apiBaseUrl: '/v3/api',
  containerId: 'label-clusters-preview',
  mapboxApiKey: '',
  labelClustersEndpoint: '/labelClusters',
  labelTypesEndpoint: '/labelTypes',
  regionWithMostLabelsEndpoint: '/regionWithMostLabels',
};

let labelTypeInfo = {};

export const LabelClustersPreview = {
  /**
   * Configure the label clusters preview.
   * @param {object} options - Configuration options
   * @returns {typeof LabelClustersPreview} The LabelClustersPreview object for chaining
   */
  setup(options) {
    config = Object.assign(config, options);
    return this;
  },

  /**
   * Initialize the label clusters preview map.
   * @returns {Promise} A promise that resolves when the preview is rendered
   */
  async init() {
    const container = document.getElementById(config.containerId);

    if (!container) {
      console.error(`Container element with id '${config.containerId}' not found.`);
      return Promise.reject(new Error('Container element not found'));
    }

    const loadingMessage = document.createElement('div');
    loadingMessage.className = 'loading-message';
    loadingMessage.textContent = 'Loading label clusters data...';
    container.appendChild(loadingMessage);

    try {
      const typeData = await this.fetchLabelTypes();
      labelTypeInfo = typeData.label_types.reduce((acc, type) => {
        acc[type.name] = { color: type.color, display: type.display_name, description: type.description };
        return acc;
      }, {});

      const regionData = await this.fetchRegionWithMostLabels();
      container.innerHTML = '';
      const map = await this.createMap(container, regionData);

      const clusters = await this.fetchClustersByRegionId(regionData.properties.region_id);
      this.displayClustersOnMap(map, clusters);
    } catch (error) {
      container.innerHTML = `<div class="message message-error" role="alert">Failed to load label clusters: `
        + `${util.escapeHTML(error.message)}</div>`;
      console.error('Label clusters preview error:', error);
      // The failure is already surfaced in the container above, and init() is fire-and-forget at every call
      // site (app/views/apiDocs/*), so re-rejecting here can only ever become an unhandled rejection.
    }
  },

  /**
   * Fetch label types from the API.
   * @returns {Promise} A promise that resolves with the label types data
   */
  fetchLabelTypes() {
    return ApiDocsMap.fetchJson(`${config.apiBaseUrl}${config.labelTypesEndpoint}`);
  },

  /**
   * Fetch region with the most labels.
   * @returns {Promise} A promise that resolves with the region data
   */
  fetchRegionWithMostLabels() {
    return ApiDocsMap.fetchJson(`${config.apiBaseUrl}${config.regionWithMostLabelsEndpoint}`)
      .catch((error) => {
        console.error('Error fetching region with most labels:', error);
        throw new Error('Failed to fetch region with most labels');
      });
  },

  /**
   * Fetch clusters by region ID.
   * @param {number} regionId - ID of the region
   * @returns {Promise} A promise that resolves with the clusters data
   */
  fetchClustersByRegionId(regionId) {
    return ApiDocsMap.fetchJson(`${config.apiBaseUrl}${config.labelClustersEndpoint}?regionId=${regionId}`);
  },

  /**
   * Create the map, framed on the region the preview is scoped to and with that region outlined.
   * @param {HTMLElement} container - Container element for the map
   * @param {GeoJSON.Feature} regionData - GeoJSON Feature for the region to display
   * @returns {Promise<mapboxgl.Map>} A promise that resolves with the loaded Mapbox map
   */
  async createMap(container, regionData) {
    const map = await ApiDocsMap.create({
      container,
      mapboxApiKey: config.mapboxApiKey,
      bounds: geometryBounds(regionData.geometry),
    });

    // Outline the region so it's clear which slice of the city the clusters below are drawn from.
    map.addSource(REGION_SOURCE, { type: 'geojson', data: regionData });
    map.addLayer({
      id: 'region-fill',
      type: 'fill',
      source: REGION_SOURCE,
      paint: { 'fill-color': REGION_COLOR, 'fill-opacity': 0.1 },
    });
    map.addLayer({
      id: 'region-outline',
      type: 'line',
      source: REGION_SOURCE,
      paint: { 'line-color': REGION_COLOR, 'line-width': 2, 'line-opacity': 0.7 },
    });

    const regionTitle = ApiDocsMap.addOverlay(map, 'top-right', 'map-chip');
    const regionName = regionData.properties.name || 'Sample Region';
    regionTitle.innerHTML = `<strong>Region:</strong> ${util.escapeHTML(regionName)}`;

    return map;
  },

  /**
   * Display clusters on the map.
   * @param {mapboxgl.Map} map - The Mapbox map object
   * @param {GeoJSON.FeatureCollection} clusters - GeoJSON data containing the label clusters
   */
  displayClustersOnMap(map, clusters) {
    if (!clusters.features || clusters.features.length === 0) {
      const noClustersDiv = document.createElement('div');
      noClustersDiv.className = 'map-message';
      noClustersDiv.setAttribute('role', 'status');
      noClustersDiv.textContent = 'No label clusters found in this region.';
      map.getContainer().appendChild(noClustersDiv);
      return;
    }

    map.addSource(CLUSTERS_LAYER, { type: 'geojson', data: clusters });
    map.addLayer({
      id: CLUSTERS_LAYER,
      type: 'circle',
      source: CLUSTERS_LAYER,
      paint: {
        // Bigger circle for a bigger cluster, capped so a heavily-labeled corner doesn't swallow its neighbors.
        'circle-radius': ['min', 8, ['+', 3, ['*', 0.5, ['coalesce', ['get', 'cluster_size'], 1]]]],
        'circle-color': ApiDocsMap.labelTypeColorExpression(labelTypeInfo),
        'circle-opacity': 0.75,
        'circle-stroke-color': ApiDocsTheme.color('--color-neutral-black'),
        'circle-stroke-width': 1,
      },
    });

    this.addClusterPopups(map);

    const countChip = ApiDocsMap.addOverlay(map, 'top-right', 'map-chip');
    countChip.textContent = `Showing ${clusters.features.length} clusters`;

    // The legend lists only the types actually drawn, so it never advertises one this region has none of.
    const typesInData = [...new Set(clusters.features.map((feature) => feature.properties.label_type))];
    const legend = ApiDocsMap.addOverlay(map, 'bottom-left', 'map-legend');
    ApiDocsMap.renderLabelTypeLegend(legend, 'Label Types', typesInData, labelTypeInfo,
      'No clusters in this region');
  },

  /**
   * Wire up the click popup and hover cursor for the cluster layer.
   * @param {mapboxgl.Map} map - The Mapbox map object
   */
  addClusterPopups(map) {
    map.on('click', CLUSTERS_LAYER, (e) => {
      const feature = e.features[0];
      const props = feature.properties;

      const severity = props.median_severity
        ? `Median Severity: ${util.escapeHTML(props.median_severity)}`
        : 'No severity rating';
      const avgLabelDate = props.avg_label_date
        ? `Avg. Label Date: ${new Date(props.avg_label_date).toLocaleDateString()}`
        : 'Unknown date';
      const clusterSize = `Cluster Size: ${util.escapeHTML(props.cluster_size)} labels`;
      const validation = `Validation: ${util.escapeHTML(props.agree_count)} agree, `
        + `${util.escapeHTML(props.disagree_count)} disagree, ${util.escapeHTML(props.unsure_count)} unsure`;

      ApiDocsMap.popup(map, feature.geometry.coordinates.slice(), `
        <h4>${util.escapeHTML(labelTypeInfo[props.label_type]?.display) || util.escapeHTML(props.label_type)}</h4>
        <p>${util.escapeHTML(labelTypeInfo[props.label_type]?.description || '')}</p>
        <p>${severity}</p>
        <p>${clusterSize}</p>
        <p>${avgLabelDate}</p>
        <p>${validation}</p>
        <p>Cluster ID: ${util.escapeHTML(props.label_cluster_id)}</p>
      `);
    });

    map.on('mouseenter', CLUSTERS_LAYER, () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', CLUSTERS_LAYER, () => {
      map.getCanvas().style.cursor = '';
    });
  },
};
